import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import DataComponent from '../../../DataComponent/DataComponent';
import CustomTooltip from '../../../Tooltip';
import { I18nContext } from 'utils/I18n/context';

const images = {
    x: require('img/icons/x.svg'),
};

const minCountExtraParams = 2;

const PostbackParameters = ({ 
    headerNames, 
    errors, 
    data, 
    loading, 
    getPostbackParameters, 
    onAction 
}) => {
    const { t } = useContext(I18nContext);
    const [state, setState] = useState({
        extra: data?.extra || [],
        list: data?.list || [],
        selected: data?.selected || []
    });

    const staticNames = t?.postbackPage?.creatingEditing || { 
        paramsTitle: 'Parameters', 
        parameterError: 'Parameter error', 
        paramsExtraTitle: 'Extra Parameters', 
        paramExtraTitle: 'Extra Parameter' 
    };
    const staticNameActions = t?.actions || { delete: 'Delete' };

    const onChange = useCallback(() => {
        onAction(state);
    }, [state, onAction]);

    const unique = useCallback((extra = [], selected = []) => {
        extra.map((itemExtra) => {
            if (!itemExtra.name || !itemExtra.value) return;

            const index = selected.findIndex((item) => itemExtra.value === item.value);

            if (index === -1) {
                selected.push(itemExtra);
            }
        });
    }, []);

    const getExtra = useCallback((extra = []) => {
        const count = Math.max(extra.length, state.extra.length, minCountExtraParams);
        let list = [];

        for (let i = 0; i < count; i++) {
            if (extra[i]) {
                list.push({...extra[i]});
            } else if (state.extra[i]) {
                list.push(state.extra[i]);
            } else {
                list.push({name: '', value: ''});
            }
        }

        return list;
    }, [state.extra]);

    useEffect(() => {
        if (!data?.list?.length) {
            getPostbackParameters();
        }

        setState(prev => ({
            ...prev,
            extra: getExtra()
        }));

        if (data?.list?.length) {
            onChange();
        }
    }, [data?.list?.length, getPostbackParameters, getExtra, onChange]);

    useEffect(() => {
        const extra = getExtra(data?.extra);
        unique(extra, data?.selected);

        setState(prev => ({
            ...prev,
            list: data?.list || [],
            selected: data?.selected || [],
            extra: extra
        }));

        onChange();
    }, [data, getExtra, unique, onChange]);

    const onDataTableAction = useCallback((state, actionType) => {
        // Empty implementation
    }, []);

    const onChangeField = useCallback((event, index, keyState) => {
        let list = state[keyState];
        const name = event.target.name;
        const value = event.target.value;

        if (keyState === 'list') {
            list[index].data[name] = value;
        } else {
            list[index][name] = value;
        }

        setState(prev => ({
            ...prev,
            [keyState]: list
        }), () => {
            updateSelected();
        });
    }, [state]);

    const onChangeSelected = useCallback((event) => {
        const checked = event.target.checked;
        const index = event.target.value;
        let list = state.list;

        list[index].checked = checked;

        setState(prev => ({
            ...prev,
            list
        }), () => {
            updateSelected();
        });
    }, [state.list]);

    const updateSelected = useCallback(() => {
        let selected = [];

        state.list.map((item) => {
            if (item.checked) {
                selected.push({
                    name: item.data.name,
                    value: item.data.macros
                });
            }
        });

        state.extra.map((item) => {
            if (item.name && item.value) {
                selected.push(item);
            }
        });

        setState(prev => ({
            ...prev,
            selected
        }), () => {
            onChange();
        });
    }, [state.list, state.extra, onChange]);

    const onAddParameter = useCallback(() => {
        let list = state.extra;

        list.push({
            name: '',
            value: ''
        });

        setState(prev => ({
            ...prev,
            extra: list
        }), () => {
            onChange();
        });
    }, [state.extra, onChange]);

    const onDeleteParameter = useCallback((index) => {
        let list = state.extra;

        list.splice(index, 1);

        setState(prev => ({
            ...prev,
            extra: list
        }), () => {
            updateSelected();
        });
    }, [state.extra, updateSelected]);

    const getParameters = useCallback(() => {
        let list = [];

        state.list.map((item, i) => {
            list.push({
                checked: (
                    <label className="option">
                        <input
                            className="option__input"
                            type="checkbox"
                            name={item.data.name}
                            value={i}
                            checked={item.checked}
                            onChange={onChangeSelected}
                        />
                        <span className="option__mark" />
                        <span className="option__caption" />
                    </label>
                ),
                variableName: (
                    <input
                        className="form-control"
                        type="text"
                        name="name"
                        value={item.data.name}
                        onChange={(event) => { onChangeField(event, i, 'list'); }}
                    />
                ),
                valueVariable: item.data.title,
                macros: item.data.macros
            });
        });

        return list;
    }, [state.list, onChangeSelected, onChangeField]);

    const getExtraParameters = useCallback(() => {
        let list = [];
        const newBtn = (
            <button className="addButton" onClick={onAddParameter} type="button">
                <img src={images.x} alt="" />
            </button>
        );

        state.extra.map((item, i) => {
            if (i === 0 || i < state.extra.length-1 || item.name.length > 0) {
                list.push({
                    title: <h3 className="bold subtitle"> {staticNames.paramExtraTitle} </h3>
                })
                list.push({
                    variableName: (
                        <input
                            className="form-control"
                            type="text"
                            name="name"
                            value={item.name}
                            onChange={(event) => { onChangeField(event, i, 'extra'); }}
                        />
                    ),
                    valueVariable: (
                        <input
                            className="form-control"
                            type="text"
                            name="value"
                            value={item.value}
                            onChange={(event) => { onChangeField(event, i, 'extra'); }}
                        />
                    ),
                    add: newBtn,
                    checked: (
                        <div className="row-controls">
                            <CustomTooltip id={`controlDelete-${i}`} tooltip={staticNameActions.delete}>
                                <a
                                    className="control-item deleteButton"
                                    href="#!"
                                    onClick={() => { onDeleteParameter(i); }}
                                >
                                    <img src={images.x} alt="" />
                                </a>
                            </CustomTooltip>
                        </div>
                    )
                });
            }
        });
        return list;
    }, [state.extra, onAddParameter, onChangeField, onDeleteParameter, staticNames.paramExtraTitle, staticNameActions.delete]);

    return (
        <div className="postback__table">
            <h2 className="bold">{staticNames.paramsTitle}</h2>
            {errors?.length
                ? (
                    <div className="has-error">
                        <div className="control-label">{staticNames.parameterError}</div>
                        <br/>
                    </div>
                )
                : null
            }
            <DataComponent
                onAction={onDataTableAction}
                headerNames={headerNames}
                data={getParameters()}
                loading={loading}
                totalPages={1}
                className="simple-table"
            />
            <div className="postback__add-param-wrapper">
                <h3 className="bold">{staticNames.paramsExtraTitle}</h3>
            </div>
            {state.extra.length
                ? (<div className="postback__extra">
                    <DataComponent
                        onAction={onDataTableAction}
                        data={getExtraParameters()}
                        totalPages={1}
                    />
                </div>)
                : null
            }
        </div>
    );
};

PostbackParameters.propTypes = {
    headerNames: PropTypes.array,
    errors: PropTypes.array,
    data: PropTypes.shape({
        list: PropTypes.array,
        selected: PropTypes.array,
        extra: PropTypes.array
    }),
    loading: PropTypes.bool,
    getPostbackParameters: PropTypes.func,
    onAction: PropTypes.func
};

PostbackParameters.defaultProps = {
    headerNames: [],
    errors: [],
    data: {
        list: [],
        selected: [],
        extra: []
    },
    loading: false,
    getPostbackParameters: () => {},
    onAction: () => {}
};

export { PostbackParameters };
